import apiClient, { API_BASE_URL } from './apiClient';

// ============================================================
// Types — matching backend Go models
// ============================================================

export interface FC110 {
  fc110_01: string;   // S/C No (PK)
  fc110_10: string;   // Dept code
  fc110_11: string | null; // S/C date (ISO string)
  fc110_12: string;   // Customer code
  fc110_13: string;   // Salesperson
  fc110_14: string;   // Customer order no
  fc110_15: string | null; // L/C last date
  fc110_16: string;   // Middleman
  fc110_17: string;   // Garment buyer
  fc110_18: string;   // Entry person
  fc110_20: string;   // Delivery location
  fc110_21: string;   // Quotation no
  fc110_211: number;  // Quotation type
  fc110_22: string;   // Payment terms
  fc110_24: string;   // Currency
  fc110_25: number;   // Exchange rate
  fc110_26: string;   // Sales sample no
  fc110_27: string;   // Remark
  fc110_271: string;  // Remark template code
  fc110_28: string;   // Shipping mark (memo)
  fc110_30: number;   // Total items
  fc110_32: number;   // Sales amount (NTD)
  fc110_33: number;   // Purchase amount
  fc110_34: number;   // Processing fee
  fc110_35: number;   // Expense
  fc110_36: number;   // Commission expense
  fc110_37: number;   // Commission income
  fc110_39: boolean;  // Credit check
  fc110_51: string;   // Approval status
  fc110_53: string;   // Close status
  fc110_58: number;   // Order category (1=大貨, 2=樣品)
  fc110_59: string;   // Performance customer
  fc110_60: string;   // Brand
  fc110_63: string | null; // Entry date
  fc110_66: string | null; // Customer order date
  fc110_71: string;   // Cancel status
  fc110_74: string;   // Visibility
  fc110_75: boolean;  // Internal transaction
  fc110_90: number;   // Version
  fc110_91: string;   // Modify status

  // Associations
  details?: FC111[];
  shipping_marks?: FC118[];
}

export interface FC111 {
  fc111_01: string;   // S/C No (FK)
  fc111_02: number;   // Item seq (PK part)
  fc111_10: string;   // Product code 1
  fc111_11: string;   // Product code 2
  fc111_12: string | null; // Delivery date
  fc111_121: string | null; // Purchase delivery date
  fc111_13: number;   // Sales qty
  fc111_14: string;   // Unit
  fc111_15: number;   // Unit price
  fc111_16: number;   // Sales amount
  fc111_17: number;   // Payable comm rate
  fc111_18: number;   // Payable comm basis
  fc111_19: number;   // Comm adjustment
  fc111_20: number;   // Comm total
  fc111_21: string;   // Product spec (memo)
  fc111_22: number;   // Recv comm rate
  fc111_23: number;   // Recv comm basis
  fc111_25: number;   // Recv comm total
  fc111_26: number;   // Fabric type
  fc111_27: string;   // Product usage
  fc111_28: string;   // Order type
  fc111_29: string;   // Trade terms
  fc111_30: string;   // Packing
  fc111_301: string;  // Packing desc
  fc111_36: number;   // Purchase price
  fc111_37: number;   // Purchase amount
  fc111_41: string;   // Purchase currency
  fc111_42: number;   // Purchase exchange rate
  fc111_50: number;   // Processing fee
  fc111_51: number;   // Unit gross profit
  fc111_54: number;   // Expense amount
  fc111_55: string;   // Customer product code
  fc111_57: string;   // AS. flag

  // Certification checkboxes (BUG #49)
  fc111_grs?: boolean;       // GRS certification
  fc111_bluesign?: boolean;  // Bluesign certification
  fc111_oekotex?: boolean;   // Oeko-tex certification

  // Sub-detail associations
  expenses?: FC112[];
  materials?: FC113[];
  commissions?: FC114[];
  colors?: FC115[];
  inspections?: FC116[];
  accessories?: FC117[];
}

export interface FC112 {
  fc112_01: string;
  fc112_02: number;
  fc112_03: number;
  fc112_11: string;   // Expense code
  fc112_12: number;   // Amount
}

export interface FC113 {
  fc113_01: string;
  fc113_02: number;
  fc113_03: number;
  fc113_11: string;
  fc113_13: number;
  fc113_14: number;
  fc113_15: number;
  fc113_16: number;
  fc113_17: number;
  fc113_18: string;
}

export interface FC114 {
  fc114_01: string;
  fc114_02: number;
  fc114_03: number;
  fc114_11: string;   // Company code
  fc114_12: string;   // Company name
  fc114_13: string;   // Recv comm currency
  fc114_14: number;   // Recv comm rate
  fc114_15: number;   // Recv comm amount
  fc114_16: string;   // Pay comm currency
  fc114_17: number;   // Pay comm rate
  fc114_18: number;   // Pay comm amount
}

export interface FC115 {
  fc115_01: string;
  fc115_02: number;
  fc115_03: number;
  fc115_11: string;   // Color code
  fc115_14: number;   // Color qty
}

export interface FC116 {
  fc116_01: string;
  fc116_02: number;
  fc116_03: number;
  fc116_11: string;   // Test code
  fc116_12: number;
  fc116_13: string;
  fc116_14: string;
}

export interface FC117 {
  fc117_01: string;
  fc117_02: number;
  fc117_03: number;
  fc117_11: string;
  fc117_12: string;
  fc117_13: string;
  fc117_14: string;
  fc117_15: string;
}

export interface FC118 {
  fc118_01: string;
  fc118_02: number;
  fc118_11: string;   // Shipping mark line
}

// ============================================================
// Request / Response types
// ============================================================

export interface SCListResponse {
  data: FC110[];
  total: number;
  page: number;
  size: number;
}

export interface SCCreateRequest {
  master: Partial<FC110>;
  details: Partial<FC111>[];
}

export interface ValidationError {
  field: string;
  rule: string;
  message: string;
}

// ============================================================
// API methods
// ============================================================

// ============================================================
// Approval types
// ============================================================

export interface ApprovalResponse {
  sc_no: string;
  action: string;       // SUBMIT | APPROVE | REJECT
  old_status: string;
  new_status: string;
  message: string;
}

export interface ApprovalHistory {
  id: number;
  sc_no: string;
  action: string;       // SUBMIT | APPROVE | REJECT
  actor_id: string;
  actor_name: string;
  comment: string;
  old_status: string;
  new_status: string;
  created_at: string;   // ISO string
}

export interface SCSearchResult {
  sc_no: string;
  customer_code: string;
  customer_name: string;
  sc_date: string | null;
  salesperson_id: string;
  salesperson: string;
  customer_order: string;
  currency: string;
}

export interface SCSearchResponse {
  data: SCSearchResult[];
  total: number;
  page: number;
  size: number;
}

export const scApi = {
  async search(keyword: string, approvedOnly: boolean, page = 1, size = 20, dateFrom?: string, dateTo?: string): Promise<SCSearchResponse> {
    const params: Record<string, string | number> = { keyword, page, size };
    if (approvedOnly) params.approvedOnly = 'true';
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    const res = await apiClient.get('/api/sc/search', { params });
    return res.data;
  },

  async list(page = 1, size = 20): Promise<SCListResponse> {
    const res = await apiClient.get('/api/sc', { params: { page, size } });
    return res.data;
  },

  async get(scNo: string): Promise<FC110> {
    const res = await apiClient.get(`/api/sc/${encodeURIComponent(scNo)}`);
    return res.data;
  },

  async create(data: SCCreateRequest): Promise<FC110> {
    const res = await apiClient.post('/api/sc', data);
    return res.data;
  },

  async update(scNo: string, data: SCCreateRequest): Promise<FC110> {
    const res = await apiClient.put(`/api/sc/${encodeURIComponent(scNo)}`, data);
    return res.data;
  },

  async delete(scNo: string): Promise<void> {
    await apiClient.delete(`/api/sc/${encodeURIComponent(scNo)}`);
  },

  // Copy: POST /api/sc/:id/copy — BR-D02
  async copy(scNo: string): Promise<FC110> {
    const res = await apiClient.post(`/api/sc/${encodeURIComponent(scNo)}/copy`);
    return res.data;
  },

  // Preview: 取得 PDF 預覽的完整 URL（含 token），用於 window.open 直接開啟
  // Chrome/Edge 都會用內建 PDF 檢視器渲染
  // options: 可選的列印選項（表頭類型、格式、A4 等），傳給後端產生對應 PDF
  getPrintPreviewUrl(scNo: string, reportType: string = 'P1', options?: {
    headerType?: string;
    format?: string;
    useA4Format?: boolean;
    approverId?: string;
    reports?: string;  // 多報表合併模式：如 "P1,P3,P4"
  }): string {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
    const params = new URLSearchParams({ token });
    // 多報表合併模式用 reports 參數，單報表用 type 參數
    if (options?.reports) {
      params.set('reports', options.reports);
    } else {
      params.set('type', reportType);
    }
    // 附加列印選項參數
    if (options?.headerType) params.set('headerType', options.headerType);
    if (options?.format) params.set('format', options.format);
    if (options?.useA4Format) params.set('useA4Format', 'true');
    if (options?.approverId) params.set('approverId', options.approverId);
    return `${API_BASE_URL}/api/sc/${encodeURIComponent(scNo)}/print?${params.toString()}`;
  },

  // 合併列印：POST /api/sc/:id/print，傳送 PrintOptions，回傳合併的 PDF（ArrayBuffer）
  async printWithOptions(scNo: string, options: {
    headerType: string;
    format: string;
    printSCReport: boolean;
    printColorSample: boolean;
    printInspection: boolean;
    useA4Format: boolean;
    approverId?: string;
  }): Promise<Blob> {
    const res = await apiClient.post(
      `/api/sc/${encodeURIComponent(scNo)}/print`,
      options,
      {
        responseType: 'blob',
        headers: { Accept: 'application/pdf' },
      }
    );
    return res.data;
  },

  // ============================================================
  // Approval workflow
  // ============================================================

  // Submit for approval: POST /api/sc/:id/submit — BR-U11
  async submitForApproval(scNo: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/sc/${encodeURIComponent(scNo)}/submit`, { comment: comment || '' });
    return res.data;
  },

  // Approve: POST /api/sc/:id/approve
  async approve(scNo: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/sc/${encodeURIComponent(scNo)}/approve`, { comment: comment || '' });
    return res.data;
  },

  // Reject: POST /api/sc/:id/reject
  async reject(scNo: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/sc/${encodeURIComponent(scNo)}/reject`, { comment: comment || '' });
    return res.data;
  },

  // Writeoff: POST /api/sc/:id/writeoff
  async writeoff(scNo: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/sc/${encodeURIComponent(scNo)}/writeoff`, { comment: comment || '' });
    return res.data;
  },

  // Approval history: GET /api/sc/:id/approval-history — BR-U10
  async getApprovalHistory(scNo: string): Promise<ApprovalHistory[]> {
    const res = await apiClient.get(`/api/sc/${encodeURIComponent(scNo)}/approval-history`);
    return res.data.data || [];
  },

  // Lookup: get display name for a reference table code
  // type: employee | customer | department | currency | paymentTerms | packing | tradeTerms | remarkTemplate
  async lookupName(type: string, code: string): Promise<string> {
    if (!code || !code.trim()) return '';
    try {
      const res = await apiClient.get(`/api/sc/lookup/${encodeURIComponent(type)}/${encodeURIComponent(code.trim())}`);
      return res.data.name || '';
    } catch {
      return '';
    }
  },
};
